import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DescriptionFields from "./DescriptionFields";
import {
	createPattern,
	getPatternById,
	updatePattern,
} from "../../../actions/patternActions";
import { Grid, Form, Container, Message, Icon } from "semantic-ui-react";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import Title from "./Title";
import { FormattedMessage, useIntl } from "react-intl";
import { PatternDifficulty, PreviewPhoto } from "../../../types/siteTypes";
import ProductSection from "./ProductSection";
import PriceInputs from "./PriceInputs";
import { clients, categories, fabrics, sizes } from "../../../utils/siteValues";
import UploadPhoto from "../../../components/UploadPhoto";
import isEmpty from "../../../validation/is-empty";
import { patternSelector, profileSelector, userIsAdminSelector } from "../../../utils/selectors";
import Spinner from "../../../components/Spinner";
import styles from "./ManagePattern.module.scss";
import { getPreferredCurrencyFromLanguage } from "../../../utils/currency";
import UploadPDF, { PreviewPDF } from "../../../components/UploadPDF/UploadPDF";

type Props = {
	userId?: string;
	goback?: boolean;
};

const parseNumericInput = (value: unknown): number => {
	if (typeof value === "number") {
		return value;
	}

	if (typeof value !== "string") {
		return Number.NaN;
	}

	const normalized = value.replace(",", ".").trim();

	if (!normalized) {
		return Number.NaN;
	}

	return Number(normalized);
};

const normalizeDifficulty = (value: unknown): PatternDifficulty => {
	const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";

	if (normalized === "easy" || normalized === "hard" || normalized === "medium") {
		return normalized;
	}

	return "medium";
};

const getEffectivePrice = (
	price: unknown,
	discount: unknown,
): number | null => {
	const numericPrice = parseNumericInput(price);
	const numericDiscount = parseNumericInput(discount ?? 0);

	if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
		return null;
	}

	if (
		Number.isFinite(numericDiscount) &&
		numericDiscount > 0 &&
		numericDiscount < numericPrice
	) {
		return numericDiscount;
	}

	return numericPrice;
};

const getDraftOmnibusReference = (
	pattern: any,
	periodDays: number = 30,
): number | null => {
	if (!pattern) {
		return null;
	}

	const now = new Date();
	const sourceCurrency = String(pattern.currency || "EUR").toUpperCase();

	const points = (
		Array.isArray(pattern.priceHistory) ? pattern.priceHistory : []
	)
		.map((entry: any) => {
			const changedAt = new Date(entry.changedAt);
			const effectivePrice = getEffectivePrice(entry.price, entry.discount);
			const currency = String(entry.currency || sourceCurrency).toUpperCase();

			return {
				changedAt,
				effectivePrice,
				currency,
				price: parseNumericInput(entry.price),
				discount: parseNumericInput(entry.discount ?? 0),
			};
		})
		.filter(
			(entry: any) =>
				entry.currency === sourceCurrency &&
				entry.effectivePrice !== null &&
				Number.isFinite(entry.effectivePrice) &&
				Number.isFinite(entry.changedAt.getTime()),
		)
		.sort((a: any, b: any) => a.changedAt.getTime() - b.changedAt.getTime());

	const currentSnapshot = {
		changedAt: now,
		effectivePrice: getEffectivePrice(pattern.price, pattern.discount),
		currency: sourceCurrency,
		price: parseNumericInput(pattern.price),
		discount: parseNumericInput(pattern.discount ?? 0),
	};

	if (currentSnapshot.effectivePrice !== null) {
		const lastPoint = points[points.length - 1];
		const hasSameLastPoint =
			lastPoint &&
			parseNumericInput((lastPoint as any).price) === currentSnapshot.price &&
			parseNumericInput((lastPoint as any).discount) ===
			currentSnapshot.discount &&
			lastPoint.currency === currentSnapshot.currency;
		if (!hasSameLastPoint) {
			points.push(currentSnapshot);
		}
	}

	const windowEnd = now;
	const windowStart = new Date(
		windowEnd.getTime() - periodDays * 24 * 60 * 60 * 1000,
	);

	const candidatesInWindow = points.filter(
		(entry: any) =>
			entry.changedAt >= windowStart && entry.changedAt <= windowEnd,
	);
	const stateBeforeWindow = [...points]
		.reverse()
		.find((entry: any) => entry.changedAt < windowStart);

	const candidates = [...candidatesInWindow];
	if (stateBeforeWindow) {
		candidates.push(stateBeforeWindow);
	}

	const persistedDiscount = parseNumericInput(pattern.discount ?? 0);
	const persistedPrice = parseNumericInput(pattern.price);
	const persistedIsDiscounted =
		Number.isFinite(persistedDiscount) &&
		Number.isFinite(persistedPrice) &&
		persistedDiscount > 0 &&
		persistedDiscount < persistedPrice;

	// New product published directly with discount: use base price as reference.
	if (candidates.length === 0 && persistedIsDiscounted) {
		candidates.push({
			changedAt: windowEnd,
			effectivePrice: persistedPrice,
			currency: sourceCurrency,
		});
	}

	if (candidates.length === 0) {
		return null;
	}

	return Math.min(
		...candidates.map((entry: any) => Number(entry.effectivePrice)),
	);
};

const isDiscountActive = (price: unknown, discount: unknown): boolean => {
	const numericPrice = parseNumericInput(price);
	const numericDiscount = parseNumericInput(discount ?? 0);

	return (
		Number.isFinite(numericPrice) &&
		numericPrice > 0 &&
		Number.isFinite(numericDiscount) &&
		numericDiscount > 0 &&
		numericDiscount < numericPrice
	);
};

const hasDraftPricingChanged = (
	pattern: any,
	draftPriceValue: unknown,
	draftDiscountValue: unknown,
	draftCurrencyValue: unknown,
	isDiscountEnabled: boolean,
): boolean => {
	const toCents = (value: number): number | null =>
		Number.isFinite(value) ? Math.round(value * 100) : null;

	if (!pattern) {
		return false;
	}

	const persistedPrice = Number(pattern.price);
	const persistedDiscount = Number(pattern.discount ?? 0);
	const persistedCurrency = String(pattern.currency || "EUR").toUpperCase();

	const draftPrice = parseNumericInput(draftPriceValue);
	const draftDiscount = isDiscountEnabled
		? parseNumericInput(draftDiscountValue ?? 0)
		: 0;
	const draftCurrency = String(draftCurrencyValue || "EUR").toUpperCase();

	if (!Number.isFinite(draftPrice) || !Number.isFinite(draftDiscount)) {
		return true;
	}

	return (
		toCents(persistedPrice) !== toCents(draftPrice) ||
		toCents(persistedDiscount) !== toCents(draftDiscount) ||
		persistedCurrency !== draftCurrency
	);
};

const formatCurrency = (value: number, currency: string): string =>
	new Intl.NumberFormat("pl-PL", {
		style: "currency",
		currency: currency === "EUR" ? currency : "EUR",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);

const ManagePattern: React.FC<Props> = ({ goback = true }) => {
	const [pdfList, setPdfList] = React.useState<PreviewPDF[]>([]);
	const intl = useIntl();
	const navigate = useNavigate();
	const { id: productId } = useParams<{ id: string }>();
	const dispatch = useDispatch();
	const defaultCurrency = getPreferredCurrencyFromLanguage();
	const { _id, role, loading: userLoading } = useSelector((state: any) => state.auth?.user)
	const isCreator = role.includes("creator");
	const isCreatorFromProfileUpdate = useSelector((state: any) => state.profile?.profile?.user?.role?.includes("creator"));
	const isAdmin = useSelector((state) => userIsAdminSelector(state));
	const isOnlyCustomer = !isCreatorFromProfileUpdate && !isCreator && !isAdmin;

	const { loading: profileLoading } = useSelector((state) =>
		profileSelector(state),
	);
	// Scroll to top when component mounts (e.g. when navigating from HomePage)
	useEffect(() => {
		document.body.scrollTo({ top: 0, behavior: "instant" });
	}, []);

	useEffect(() => {
		if (productId && !isEmpty(productId)) {
			dispatch(getPatternById(productId));
		}
	}, [productId, dispatch]);

	const { pattern, loading } = useSelector((state) => patternSelector(state));
	const [patternData, setPatternData] = useState<any>({
		name: "",
		price: 0,
		discount: 0,
		currency: defaultCurrency,
		description: "",
		descriptionLong: "",
		difficulty: "medium",
		photos: [],
		pdfUrl: "",
		dateUpdated: new Date(),
		sizes: [],
		clients: [],
		categories: [],
		fabrics: [],
	});
	const [isDiscountEnabled, setIsDiscountEnabled] = useState(false);

	useEffect(() => {
		if (productId && pattern) {
			// Editing existing pattern - populate form with pattern data
			const activeDiscountOnLoad = isDiscountActive(
				pattern.price,
				pattern.discount,
			);
			setPatternData({
				name: pattern.name,
				price: pattern.price,
				discount: pattern.discount,
				currency: pattern.currency || defaultCurrency,
				description: pattern.description,
				descriptionLong: pattern.descriptionLong,
				difficulty: normalizeDifficulty(pattern.difficulty),
				photos: pattern.photos,
				dateUpdated: pattern.dateUpdated,
				sizes: pattern.sizes,
				clients: pattern.clients,
				categories: pattern.categories,
				fabrics: pattern.fabrics,
			});
			setIsDiscountEnabled(activeDiscountOnLoad);
		} else if (!productId) {
			// Creating new pattern - reset form to empty state
			setPatternData({
				name: "",
				price: 0,
				discount: 0,
				currency: defaultCurrency,
				description: "",
				descriptionLong: "",
				difficulty: "medium",
				photos: [],
				dateUpdated: new Date(),
				sizes: [],
				clients: [],
				categories: [],
				fabrics: [],
			});
			setIsDiscountEnabled(false);
		}
	}, [productId, pattern, defaultCurrency]);


	useEffect(() => {
		if (!pattern?.pdfs?.length) return;

		setPdfList(pattern.pdfs);

	}, [pattern?.pdfs]);

	const discountValidationError = useMemo(() => {
		if (!isDiscountEnabled) {
			return "";
		}

		const numericPrice = parseNumericInput(patternData.price);
		const numericDiscount = parseNumericInput(patternData.discount);

		if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
			return "Ustaw najpierw poprawną cenę bazową.";
		}

		if (!Number.isFinite(numericDiscount) || numericDiscount <= 0) {
			return "Podaj cenę po obniżce większą od 0.";
		}

		if (numericDiscount >= numericPrice) {
			return "Cena po obniżce musi być niższa od ceny bazowej.";
		}

		return "";
	}, [isDiscountEnabled, patternData.discount, patternData.price]);

	const onSubmit = (e: any) => {
		e.preventDefault();
		if (discountValidationError) {
			return;
		}
		const normalizedPrice = parseNumericInput(patternData.price);
		const normalizedDiscount = isDiscountEnabled
			? parseNumericInput(patternData.discount)
			: 0;

		if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0) {
			return;
		}

		if (
			isDiscountEnabled &&
			(!Number.isFinite(normalizedDiscount) ||
				normalizedDiscount <= 0 ||
				normalizedDiscount >= normalizedPrice)
		) {
			return;
		}

		const payload = {
			...patternData,
			pdfs: pdfList.map(({ url, name, sizeBytes }) => ({
				url,
				name,
				sizeBytes,
			})),
			price: normalizedPrice,
			discount: isDiscountEnabled ? normalizedDiscount : 0,
			difficulty: normalizeDifficulty(patternData.difficulty),
		};
		if (productId) {
			const resolvedId = pattern?._id || productId;
			dispatch(updatePattern({ ...payload, _id: resolvedId }, navigate));
		} else {
			dispatch(createPattern(payload, navigate));
		}
	};
	const onChange = (e: any) => {
		setPatternData({ ...patternData, [e.target.name]: e.target.value });
	};
	const onPriceChange = (e: any) => {
		setPatternData({
			...patternData,
			[e.target.name]: e.target.value,
		});
	};
	const onCheckboxChange = (e: string, type: string) => {
		const newData = patternData[type].includes(e)
			? patternData[type].filter((d: string) => d !== e)
			: [...patternData[type], e];
		setPatternData({ ...patternData, [type]: newData });
	};

	const onAddPhotos = (photos: PreviewPhoto[]) => {
		setPatternData({ ...patternData, photos });
	};
	const onDiscountToggle = (checked: boolean) => {
		setIsDiscountEnabled(checked);
		if (!checked) {
			setPatternData((prev: any) => ({ ...prev, discount: 0 }));
		}
	};

	const pricingHint = useMemo(() => {
		if (!isDiscountEnabled) {
			if (!productId || !pattern) {
				return null;
			}

			const pricingChanged = hasDraftPricingChanged(
				pattern,
				patternData.price,
				0,
				patternData.currency,
				false,
			);
			if (!pricingChanged) {
				return null;
			}

			const persistedEffective = getEffectivePrice(
				pattern.price,
				pattern.discount,
			);
			const draftBasePrice = parseNumericInput(patternData.price);

			if (
				persistedEffective !== null &&
				Number.isFinite(draftBasePrice) &&
				draftBasePrice > 0 &&
				draftBasePrice < persistedEffective
			) {
				return {
					tone: "info",
					header: "Obniżasz cenę bez oznaczenia promocji",
					text:
						"Zmniejszasz cenę bazową przy wyłączonej obniżce. " +
						"Oznaczenie promocji i przekreślona cena nie będą pokazane. " +
						"By promocja była widoczna zaznacz 'Włącz obniżkę'.",
				};
			}

			return null;
		}

		if (discountValidationError) {
			return {
				tone: "warning",
				header: "Uzupełnij dane obniżki",
				text: discountValidationError,
			};
		}

		const draftEffective = getEffectivePrice(
			patternData.price,
			patternData.discount,
		);

		if (draftEffective === null) {
			return null;
		}

		if (!productId || !pattern) {
			return null;
		}

		const pricingChanged = hasDraftPricingChanged(
			pattern,
			patternData.price,
			patternData.discount,
			patternData.currency,
			isDiscountEnabled,
		);
		if (!pricingChanged) {
			return null;
		}

		const omnibusReference = getDraftOmnibusReference(pattern);

		const hasOmnibusReference =
			omnibusReference !== null && Number.isFinite(omnibusReference);

		if (!hasOmnibusReference) {
			return {
				tone: "warning",
				header: "Błąd danych Omnibus",
				text:
					"Oznaczenie promocji nie zostanie wyświetlony, ponieważ nie możemy odczytać " +
					"najniższej ceny z 30 dni przed obniżką. To wygląda na błąd danych. " +
					"Skontaktuj się z supportem.",
			};
		}
		const referenceValue = omnibusReference;

		if (draftEffective < referenceValue) {
			return null;
		}

		return null;
	}, [
		discountValidationError,
		isDiscountEnabled,
		pattern,
		patternData.currency,
		patternData.discount,
		patternData.price,
		productId,
	]);

	const omnibusReferenceValue = useMemo(() => {
		if (!productId || !pattern) {
			return null;
		}

		const reference = getDraftOmnibusReference(pattern);
		return reference !== null && Number.isFinite(reference) ? reference : null;
	}, [pattern, productId]);

	const omnibusReferenceText = useMemo(() => {
		if (omnibusReferenceValue === null) return "";
		const currency = pattern?.currency || patternData.currency || "EUR";
		return formatCurrency(omnibusReferenceValue, currency);
	}, [omnibusReferenceValue, pattern?.currency, patternData.currency]);

	if (userLoading || profileLoading) {
		return <Spinner />;
	}

	// If no profile and not editing an existing pattern, show message
	if (isOnlyCustomer) {
		return (
			<Container className={styles.container}>
				<div className={styles.noProfileMessage}>
					<Message icon warning size="large">
						<Icon name="exclamation triangle" />
						<Message.Content>
							<Message.Header>
								<FormattedMessage
									id="managePattern.noProfile.title"
									defaultMessage="Profile Required"
								/>
							</Message.Header>
							<p>
								<FormattedMessage
									id="managePattern.noProfile.message"
									defaultMessage="You need to create a creator profile before you can add patterns. Your profile helps customers learn about you and builds trust.To be creator, please go to your profile and click 'Become a Creator' button."
								/>
							</p>
							<Link to={`/profile/${_id}`} className={styles.createProfileLink}>
								<PrimaryButton
									text={
										<FormattedMessage
											id="managePattern.noProfile.button"
											defaultMessage="Create Your Profile Now"
										/>
									}
								/>
							</Link>
							{goback && (
								<div className={styles.backLinkWrapper}>
									<Link to={`/profile/${_id}`} className={styles.backLink}>
										<FormattedMessage
											id="site.goBackToProfile"
											defaultMessage="Go back to Profile"
										/>
									</Link>
								</div>
							)}
						</Message.Content>
					</Message>
				</div>
			</Container>
		);
	}

	if (!isEmpty(productId) && loading) {
		return <Spinner />;
	}

	const patternIsEditable = !!pattern?.isEditable;
	const previewUrl = pattern?.previewUrl;
	const previewLabel = patternIsEditable
		? "Studio Draft Preview"
		: "Design Preview";

	return (
		<Container className={styles.container}>
			<Grid.Row centered>
				<Grid.Column computer={12}>
					{goback && (
						<div className={styles.header}>
							<Link to={isAdmin ? "/admin/patterns" : `/profile/${_id}`} className={styles.backLink}>
								<FormattedMessage id={"site.goBack"} defaultMessage={"Go back"} />
							</Link>
							<Title update={!!productId} />
						</div>
					)}
					<Form onSubmit={onSubmit}>
						{/* Top Section: Description Fields and Image Upload */}
						<div className={styles.topSection}>
							<h3 className={styles.sectionTitle}>Product Information</h3>
							<Grid columns={2} stackable>
								<Grid.Row>
									<Grid.Column>
										<DescriptionFields
											onChange={onChange}
											patternData={patternData}
										/>
									</Grid.Column>
									<Grid.Column>
										{/* Show Studio Draft Preview if available */}
										{previewUrl && (
											<div
												style={{ marginBottom: "16px", textAlign: "center" }}
											>
												<img
													src={previewUrl}
													alt={previewLabel}
													style={{
														maxWidth: "100%",
														borderRadius: "8px",
														boxShadow: "0 2px 8px #ccc",
													}}
												/>
												<div
													style={{
														fontSize: "12px",
														color: "#666",
														marginTop: "4px",
													}}
												>
													{previewLabel}
												</div>
											</div>
										)}
										<UploadPhoto
											photos={patternData.photos}
											updatePhotoList={onAddPhotos}
										/>

										<UploadPDF
											pdfs={pdfList}
											updatePDFList={(updated) => setPdfList(updated)}

										/>
									</Grid.Column>
								</Grid.Row>
							</Grid>
						</div>

						{/* Category Sections */}
						<div className={styles.categorySection}>
							<h3 className={styles.sectionTitle}>Categories & Details</h3>
							<Grid columns={2} stackable>
								<Grid.Row>
									<Grid.Column>
										<ProductSection
											categoryTypes={clients}
											title={intl.formatMessage({
												id: "categories.clients.title",
												defaultMessage: "For who",
											})}
											checkboxType={"clients"}
											onChange={onCheckboxChange}
											data={patternData.clients}
										/>
										<ProductSection
											categoryTypes={categories}
											title={intl.formatMessage({
												id: "categories.categories.title",
												defaultMessage: "Category",
											})}
											checkboxType={"categories"}
											onChange={onCheckboxChange}
											data={patternData.categories}
										/>
									</Grid.Column>
									<Grid.Column>
										<ProductSection
											categoryTypes={fabrics}
											title={intl.formatMessage({
												id: "categories.fabrics.title",
												defaultMessage: "Fabric",
											})}
											checkboxType={"fabrics"}
											onChange={onCheckboxChange}
											data={patternData.fabrics}
										/>
										<ProductSection
											categoryTypes={sizes}
											title={intl.formatMessage({
												id: "categories.sizes.title",
												defaultMessage: "Sizes",
											})}
											checkboxType={"sizes"}
											onChange={onCheckboxChange}
											data={patternData.sizes}
										/>
									</Grid.Column>
								</Grid.Row>
							</Grid>
						</div>

						<PriceInputs
							onChange={onPriceChange}
							onDiscountToggle={onDiscountToggle}
							price={patternData.price}
							discount={patternData.discount}
							isDiscountEnabled={isDiscountEnabled}
							discountError={discountValidationError}
							omnibusReferenceLabel={omnibusReferenceText}
						/>

						{pricingHint && (
							<Message
								info={pricingHint.tone === "info"}
								warning={pricingHint.tone === "warning"}
							>
								<Message.Header>{pricingHint.header}</Message.Header>
								<p>{pricingHint.text}</p>
							</Message>
						)}

						<div className={styles.submitSection}>
							<PrimaryButton role="submit" text={"Publish Pattern"} />
						</div>
					</Form>
				</Grid.Column>
			</Grid.Row>
		</Container>
	);
};

export default ManagePattern;
